import React from 'react';
import { BarChart2, BarChart, Info } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детальной разбивки налога
 */
const TaxBreakdown = ({ breakdown, results }) => {
  if (!breakdown || !breakdown.length || !results) {
    return null;
  }

  // Расчет общей суммы для визуализации соотношений
  const totalPositive = breakdown
    .filter(item => item.value > 0)
    .reduce((sum, item) => sum + item.value, 0);

  // Расчет суммы вычетов для визуализации
  const totalNegative = Math.abs(
    breakdown
      .filter(item => item.value < 0)
      .reduce((sum, item) => sum + item.value, 0)
  );

  // Определение максимальной ширины бара
  const maxBarWidth = '100%';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Разбивка налога на имущество
        </h3>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Эта диаграмма показывает, как формируется итоговая сумма налога с учетом всех вычетов и льгот.</p>
        </div>
      </div>

      {/* Визуализация разбивки налога */}
      <div className="space-y-4 mb-6">
        {/* Базовый расчет (положительные значения) */}
        {breakdown
          .filter(item => item.value > 0)
          .map((item, index) => (
            <div key={`pos-${index}`} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(item.value, 0)}</span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
                <div 
                  className="bg-indigo-500 dark:bg-indigo-400 h-2.5 rounded-full" 
                  style={{ 
                    width: totalPositive > 0 ? `${(item.value / totalPositive) * 100}%` : '0%',
                    maxWidth: maxBarWidth
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
            </div>
          ))}

        {/* Разделитель */}
        {breakdown.some(item => item.value < 0) && (
          <div className="border-t border-gray-200 dark:border-gray-700 my-4 pt-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Вычеты и льготы:
            </div>
          </div>
        )}

        {/* Вычеты и льготы (отрицательные значения) */}
        {breakdown
          .filter(item => item.value < 0)
          .map((item, index) => (
            <div key={`neg-${index}`} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                <span className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(item.value, 0)}</span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
                <div 
                  className="bg-green-500 dark:bg-green-400 h-2.5 rounded-full" 
                  style={{ 
                    width: totalNegative > 0 ? `${(Math.abs(item.value) / totalNegative) * 100}%` : '0%',
                    maxWidth: maxBarWidth
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
            </div>
          ))}

        {/* Итоговая сумма */}
        <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
          <div className="flex justify-between text-sm">
            <span className="font-bold text-gray-900 dark:text-white">Итоговая сумма налога:</span>
            <span className="font-bold text-indigo-600 dark:text-indigo-400 text-lg">{formatCurrency(results.finalTax, 0)}</span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-3 mt-2">
            <div 
              className="bg-indigo-600 dark:bg-indigo-500 h-3 rounded-full" 
              style={{ 
                width: totalPositive > 0 ? `${(results.finalTax / totalPositive) * 100}%` : '0%',
                maxWidth: maxBarWidth
              }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Сумма к уплате с учетом всех вычетов, коэффициентов и льгот
          </p>
        </div>
      </div>

      {/* Блок формулы расчета */}
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mt-4">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
          <BarChart2 className="w-4 h-4 mr-1 text-indigo-500" />
          Формула расчета налога
        </h4>
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p>
            <span className="font-mono">Налог = Кадастровая стоимость × Налоговая ставка</span>
          </p>
          
          {results.deduction > 0 && (
            <p>
              <span className="font-mono">− Налоговый вычет (на площадь {results.deductionArea} м²)</span>
            </p>
          )}
          
          {results.reducingCoefficientValue < 1 && (
            <p>
              <span className="font-mono">× Понижающий коэффициент {results.reducingCoefficientValue}</span>
            </p>
          )}
          
          {results.ownershipShareFactor < 1 && (
            <p>
              <span className="font-mono">× Доля владения {results.ownershipShareFactor}</span>
            </p>
          )}
          
          {results.ownershipPeriodFactor < 1 && (
            <p>
              <span className="font-mono">× Период владения {results.ownershipPeriodFactor}</span>
            </p>
          )}
          
          {results.privilegeDiscount > 0 && (
            <p>
              <span className="font-mono">− Налоговая льгота ({formatCurrency(results.privilegeDiscount, 0)})</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxBreakdown;